import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { User, Mail, Calendar, Shield } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function ProfilePage() {
  const { user } = useAuth();

  return (
    <div className="space-y-8 max-w-3xl animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Profile</h1>
        <p className="text-slate-500 mt-1">Manage your account settings</p>
      </div>

      {/* Profile Card */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg">Account Information</CardTitle>
          <CardDescription>Your personal details and account settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{user?.name}</h3>
              <p className="text-slate-500">{user?.email}</p>
            </div>
          </div>

          <Separator />

          {/* Details Grid */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Full Name</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900" data-testid="profile-name">{user?.name}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Email Address</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900" data-testid="profile-email">{user?.email}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Account Type</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Shield className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900 capitalize" data-testid="profile-role">{user?.role}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Member Since</Label>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900" data-testid="profile-joined">
                  {formatDate(user?.created_at)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg">Security</CardTitle>
          <CardDescription>Manage your password and security settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Password</p>
              <p className="text-sm text-slate-500">Last changed: Never</p>
            </div>
            <Button variant="outline" size="sm" disabled>
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
